'use client';
import React, { useState } from 'react';
import { Camera, MessageSquare, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { InspectionCategory, CategoryAnswers, ItemAnswer } from './inspectionData';

interface Props {
  category: InspectionCategory;
  answers: CategoryAnswers;
  onAnswersChange: (answers: CategoryAnswers) => void;
  inspectionType: string;
}

const SEVERITY_OPTIONS: Array<ItemAnswer['severity']> = ['Normal', 'Minor', 'Moderate', 'Severe', 'Critical'];
const LSD_OPTIONS: Array<1 | 2 | 3 | 4 | 5> = [1, 2, 3, 4, 5];

const LSD_LABELS: Record<number, string> = {
  1: 'Normal',
  2: 'Minor',
  3: 'Moderate',
  4: 'Severe',
  5: 'Critical',
};

const SEVERITY_TO_LSD: Record<string, 1 | 2 | 3 | 4 | 5> = {
  Normal: 1,
  Minor: 2,
  Moderate: 3,
  Severe: 4,
  Critical: 5,
};

export default function CategoryChecklistStep({ category, answers, onAnswersChange, inspectionType }: Props) {
  const [expandedObs, setExpandedObs] = useState<Set<string>>(new Set());

  const getAnswer = (ref: string): ItemAnswer => {
    return answers[ref] || { answer: null, severity: null, lsdScore: null, observation: '', photos: [] };
  };

  const updateAnswer = (ref: string, update: Partial<ItemAnswer>) => {
    const current = getAnswer(ref);
    const updated = { ...current, ...update };
    // Auto-sync LSD from severity
    if (update.severity && !update.lsdScore) {
      updated.lsdScore = SEVERITY_TO_LSD[update.severity];
    }
    onAnswersChange({ ...answers, [ref]: updated });
  };

  const toggleObs = (ref: string) => {
    setExpandedObs((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  };

  const answeredCount = category.items.filter((i) => getAnswer(i.ref).answer).length;
  const criticalCount = category.items.filter((i) => getAnswer(i.ref).severity === 'Critical' || getAnswer(i.ref).severity === 'Severe').length;

  return (
    <div className="max-w-5xl">
      {/* Category Header */}
      <div className="bg-white rounded-xl border p-5 mb-4 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ background: category.color }}>
              {category.code}
            </div>
            <div>
              <h2 className="font-bold text-base">{category.label}</h2>
              <p className="text-xs text-muted">{category.items.length} inspection items</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-lg font-bold tabular-nums">{answeredCount}<span className="text-muted font-normal text-sm">/{category.items.length}</span></p>
              <p className="text-xs text-muted">Answered</p>
            </div>
            {criticalCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: 'hsl(355 75% 58% / 0.1)' }}>
                <AlertTriangle size={14} style={{ color: 'hsl(var(--destructive))' }} />
                <span className="text-xs font-semibold" style={{ color: 'hsl(var(--destructive))' }}>
                  {criticalCount} Critical/Severe
                </span>
              </div>
            )}
            {/* Progress bar */}
            <div className="w-32 hidden sm:block">
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${(answeredCount / category.items.length) * 100}%`,
                    background: answeredCount === category.items.length ? 'hsl(var(--success))' : 'hsl(var(--primary))',
                  }}
                />
              </div>
              <p className="text-xs text-muted mt-0.5 text-right">
                {Math.round((answeredCount / category.items.length) * 100)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Checklist Items */}
      <div className="bg-white rounded-xl border shadow-card overflow-hidden" style={{ borderColor: 'hsl(var(--border))' }}>
        {/* Table Header */}
        <div className="grid px-4 py-3 border-b text-xs font-semibold uppercase tracking-wider text-muted"
          style={{ borderColor: 'hsl(var(--border))', gridTemplateColumns: '80px 1fr 160px 100px 80px 80px' }}>
          <span>Ref</span>
          <span>Inspection Item</span>
          <span className="text-center">Answer</span>
          <span className="text-center">Severity</span>
          <span className="text-center">LSD</span>
          <span className="text-center">Actions</span>
        </div>

        {category.items.map((item, idx) => {
          const ans = getAnswer(item.ref);
          const isExpanded = expandedObs.has(item.ref);
          const hasObs = ans.observation.length > 0;
          const hasPhotos = ans.photos.length > 0;

          return (
            <div
              key={`item-${item.ref}`}
              className={`checklist-row ${idx % 2 === 0 ? '' : ''}`}
              style={{ background: ans.answer === null ? 'transparent' : ans.severity === 'Critical' ? 'hsl(355 75% 58% / 0.03)' : ans.severity === 'Severe' ? 'hsl(38 92% 50% / 0.03)' : 'transparent' }}
            >
              <div className="grid gap-3 items-start"
                style={{ gridTemplateColumns: '80px 1fr 160px 100px 80px 80px' }}>
                {/* Ref */}
                <span className="font-mono text-xs font-semibold pt-0.5" style={{ color: category.color }}>{item.ref}</span>

                {/* Question */}
                <p className="text-sm leading-snug pr-2">{item.question}</p>

                {/* Answer buttons */}
                <div className="flex gap-1 justify-center">
                  {(['Yes', 'No', 'N/A'] as const).map((opt) => (
                    <button
                      key={`ans-${item.ref}-${opt}`}
                      className={`answer-btn ${ans.answer === opt ? `selected-${opt.toLowerCase().replace('/', '')}` : ''}`}
                      onClick={() => updateAnswer(item.ref, { answer: opt })}
                    >
                      {opt}
                    </button>
                  ))}
                </div>

                {/* Severity */}
                <div className="flex justify-center">
                  <select
                    className="text-xs rounded-md border px-1.5 py-1 w-full max-w-[120px] outline-none transition-all"
                    style={{ borderColor: 'hsl(var(--border))', color: 'hsl(var(--foreground))' }}
                    value={ans.severity || ''}
                    onChange={(e) => updateAnswer(item.ref, { severity: e.target.value as ItemAnswer['severity'] })}
                  >
                    <option value="">—</option>
                    {SEVERITY_OPTIONS.map((s) => (
                      <option key={`sev-${item.ref}-${s}`} value={s || ''}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* LSD Score */}
                <div className="flex justify-center">
                  <select
                    className="text-xs rounded-md border px-1.5 py-1 w-16 outline-none font-mono font-semibold transition-all"
                    style={{ borderColor: 'hsl(var(--border))' }}
                    value={ans.lsdScore || ''}
                    onChange={(e) => updateAnswer(item.ref, { lsdScore: Number(e.target.value) as ItemAnswer['lsdScore'] })}
                  >
                    <option value="">—</option>
                    {LSD_OPTIONS.map((n) => (
                      <option key={`lsd-${item.ref}-${n}`} value={n}>{n} — {LSD_LABELS[n]}</option>
                    ))}
                  </select>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 justify-center">
                  {item.hasPhoto && (
                    <button
                      title="Add photo"
                      className="p-1.5 rounded-md transition-colors"
                      style={{
                        background: hasPhotos ? 'hsl(218 70% 22% / 0.1)' : 'transparent',
                        color: hasPhotos ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                      }}
                      onClick={() => {
                        // TODO: Backend integration — trigger photo capture/upload
                        const updated = { ...ans, photos: [...ans.photos, `photo-${Date.now()}`] };
                        updateAnswer(item.ref, updated);
                      }}
                    >
                      <Camera size={14} />
                    </button>
                  )}
                  {item.hasObservation && (
                    <button
                      title="Add observation"
                      className="p-1.5 rounded-md transition-colors"
                      style={{
                        background: hasObs ? 'hsl(218 70% 22% / 0.1)' : 'transparent',
                        color: hasObs ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                      }}
                      onClick={() => toggleObs(item.ref)}
                    >
                      <MessageSquare size={14} />
                    </button>
                  )}
                  {isExpanded ? (
                    <ChevronUp size={14} className="text-muted" />
                  ) : (
                    <ChevronDown size={14} className="text-muted" />
                  )}
                </div>
              </div>

              {/* Observation & Photo expanded row */}
              {isExpanded && (
                <div className="mt-3 pl-20 pr-4 space-y-3 animate-fade-in">
                  <div>
                    <label className="block text-xs font-semibold mb-1 text-muted">Observation / Comment</label>
                    <textarea
                      className="input-field resize-none text-sm"
                      rows={3}
                      placeholder={`Add observation for ${item.ref}...`}
                      value={ans.observation}
                      onChange={(e) => updateAnswer(item.ref, { observation: e.target.value })}
                    />
                  </div>
                  {ans.photos.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold mb-2 text-muted">{ans.photos.length} photo(s) attached</p>
                      <div className="flex gap-2 flex-wrap">
                        {ans.photos.map((p, pi) => (
                          <div key={`photo-${item.ref}-${pi}`} className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                            <Camera size={20} className="text-muted" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}